import {
	classifierMetadataAtom,
	fieldValueAtom,
	isSemanticSearchCalledAtom,
} from '@components/listings-search-bar/atoms';
import { getBadgeCounts } from '@components/vendorCard/public/extractVendorProps/extract/badges';
import { getVendorsWithDealsCount } from '@components/vendorCard/public/extractVendorProps/extract/deals';
import AnalyticsConstants from '@constants/analytics';
import useAnalyticsContext from '@hooks/useAnalyticsContext';
import {
	CATEGORY_FILTER_ID,
	classifierSyncGenerationAtom,
	filterValuesAtom,
	isAllCategoriesPageAtom,
	searchQueryParamsAtom,
	selectedCategoryCodesAtom,
	wasAutoSelectedByClassifierAtom,
} from '@pages/VendorsSearch/containers/FilterPills/atoms';
import { useAppSelector } from '@redux/hooks';
import { getPageType } from '@redux/page';
import {
	resolveSelectedCategoryCodes,
	resolveVendorSearchSourcePage,
} from '@utils/cross-category-tracking';
import { useAtomValue } from 'jotai';
import { useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';

/**
 * Module-level guard that persists across component remounts.
 *
 * history.replace in useClassifierCategorySync changes the route pattern,
 * causing React Router to fully unmount/remount the component tree. A useRef
 * would reset on remount, allowing the event to fire twice or race with the
 * remount cycle. A module-level variable survives across mounts — same pattern
 * used by useClassifierCategorySync's lastAppliedClassifierSync.
 */
let hasTrackedCategoryView = false;
let lastTrackedViewKey: string | null = null;
// Last viewKey observed on *any* render, regardless of whether tracking
// fired for it. `lastTrackedViewKey` stays null when tracking is suppressed
// (e.g. the deals-page/no-semantic-search case below), which would otherwise
// make the fetch-observed stale-data guard inert for the view transition
// immediately following a suppressed page. This is tracked separately so
// that guard applies to every genuine viewKey change, independent of
// tracking history.
let lastSeenViewKey: string | null = null;

// One-time settle guard: for a given viewKey, allow exactly one early return
// when classifier payload indicates a likely in-flight auto-selection race
// (classifier_used=true and prediction matches viewed category, but
// was_auto_selected is still false). This suppresses the known false->true
// duplicate emission pattern while avoiding an indefinite block if derived
// classifier state does not settle on the next render.
const classifierAutoSelectionSettleWaitedViewKeys = new Set<string>();

// Last classifierSyncGenerationAtom value this hook has accounted for. A
// classifier-driven sync (useClassifierCategorySync, variant2/3) deliberately
// skips the real fetch, so `isFetching` never flips `true` for the resulting
// view transition and `fetchObservedForKeyRef` never gets set — see its use
// below. Module-level (not a `useRef`) for the same remount-survival reason
// as the guards above.
let lastAccountedClassifierSyncGeneration = 0;

interface SearchQueryParamsLike {
	filters?: string[];
	rangeFilters?: Array<{ field: string; gte?: number; lte?: number }>;
}

const buildStableSelectedCategoriesKey = (
	selectedCategories?: string[] | null,
): string => {
	return JSON.stringify([...(selectedCategories ?? [])].sort());
};

const buildStableSearchQueryParamsKey = (
	searchQueryParams: SearchQueryParamsLike,
): string => {
	const normalizedFilters = [...(searchQueryParams?.filters ?? [])].sort();
	const normalizedRangeFilters = [...(searchQueryParams?.rangeFilters ?? [])]
		.map((range) => ({
			field: range.field,
			gte: range.gte,
			lte: range.lte,
		}))
		.sort((a, b) => {
			const aKey = `${a.field}|${a.gte ?? ''}|${a.lte ?? ''}`;
			const bKey = `${b.field}|${b.gte ?? ''}|${b.lte ?? ''}`;
			return aKey.localeCompare(bKey);
		});

	return JSON.stringify({
		filters: normalizedFilters,
		rangeFilters: normalizedRangeFilters,
	});
};

/**
 * Reset the module-level tracking guard.
 * Clears both the tracked flag and the last-tracked view key — intended for
 * full reset between tests. See `resetTrackedFlagForNavigation` for the
 * narrower reset used by the `history.listen` effect below.
 * @internal Exported for tests.
 */
export function resetVendorCategoryViewedTracking() {
	hasTrackedCategoryView = false;
	lastTrackedViewKey = null;
	lastSeenViewKey = null;
	lastAccountedClassifierSyncGeneration = 0;
	classifierAutoSelectionSettleWaitedViewKeys.clear();
}
/**
 * Reset just the tracked flag on genuine SPA navigation (PUSH/POP), so a
 * legitimate new page view (e.g. returning to the same viewKey after leaving
 * VendorsSearch) isn't suppressed.
 *
 * Deliberately does NOT clear `lastTrackedViewKey` or `lastSeenViewKey`:
 * history.listen also fires for in-page navigations that use `history.push`
 * (e.g. Pagination changing the page number). Clearing `lastSeenViewKey` here
 * would make the next render's `isViewTransition` check compute `false`
 * (since `lastSeenViewKey` would be `null`), bypassing the
 * `fetchObservedForKeyRef` stale-data guard and allowing the event to fire
 * with the previous page's vendors/total while the new viewKey is already in
 * Redux. Leaving both keys intact keeps that viewKey-diff guard working
 * exactly as it does without this listener, while still allowing
 * same-viewKey re-visits to re-fire.
 */
function resetTrackedFlagForNavigation() {
	hasTrackedCategoryView = false;
}

/**
 * Fires 'Vendor Category Viewed' as soon as vendor data is available in Redux.
 *
 * Previously this tracking lived inside SearchResults, which only mounts after
 * isFetching becomes false. On variant arms of cross-category experiment,
 * history.replace can cause a remount cycle that delays or drops the event.
 * By placing this hook above the isFetching gate, the event fires immediately
 * when data is ready — decoupled from render gating and sibling loading.
 */
export function useVendorCategoryViewedTracking() {
	const { track } = useAnalyticsContext();
	const history = useHistory();

	const vendors = useAppSelector((state) => state.search.vendors);
	const totalVendors = useAppSelector((state) => state.search.total);
	const isFetching = useAppSelector((state) => state.search.isFetching);
	const category = useAppSelector((state) => state.category);
	const categories = useAppSelector((state) => state.categories);
	const location = useAppSelector((state) => state.location);
	const marketCode = useAppSelector((state) => state.search.marketCode);
	const selectedCategories = useAppSelector(
		(state) => state.search.selectedCategories,
	);
	const currentPage = useAppSelector((state) => state.search.pagination.page);
	const member = useAppSelector((state) => state.membership.member);
	const pageType = useAppSelector(getPageType);

	const isSemanticSearchCalled = useAtomValue(isSemanticSearchCalledAtom);
	const semanticSearchTerm = useAtomValue(fieldValueAtom);
	const wasAutoSelectedByClassifier = useAtomValue(
		wasAutoSelectedByClassifierAtom,
	);
	const isAllCategoriesPage = useAtomValue(isAllCategoriesPageAtom);
	const classifierMetadata = useAtomValue(classifierMetadataAtom);
	const searchQueryParams = useAtomValue(searchQueryParamsAtom);
	const filterValues = useAtomValue(filterValuesAtom);
	const selectedCategoryCodes = useAtomValue(selectedCategoryCodesAtom);
	const classifierSyncGeneration = useAtomValue(classifierSyncGenerationAtom);

	// SPA navigation to a new page updates Redux location/category/pagination
	// (this hook's `viewKey`) in an earlier effect than the one in
	// WrappedVendorsSearch that actually dispatches the new fetch. That leaves
	// a render where `viewKey` already reflects the new page but `isFetching`
	// is still `false` (left over from the previous page's completed fetch)
	// and `vendors`/`total` are still the previous page's data. Track whether
	// we've actually observed a fetch cycle for the current `viewKey` so a
	// stale `isFetching === false` isn't mistaken for "data ready."
	// Not applied on the very first render: SSR-rendered pages start with
	// `isFetching` already `false` and correct preloaded data, with no client
	// fetch cycle to observe.
	const isInitialRenderRef = useRef(true);
	const fetchObservedForKeyRef = useRef<string | null>(null);

	useEffect(() => {
		// Build a view-identity key so the guard auto-resets on genuine SPA
		// navigation (new location, category, or page number) *and* on filter
		// changes (new searchQueryParams — facets, ranges, etc.), while still
		// surviving the history.replace remount (same underlying view).
		// Filters are included here — rather than relying on history.listen to
		// reset the guard — so a filter change goes through the same
		// fetch-observed guard below as any other view change: it waits for the
		// filtered fetch to actually complete instead of firing immediately (or
		// with stale pre-filter data).
		const viewKey = `${history.location.pathname}|${location.city}|${location.stateCode}|${currentPage}|${buildStableSearchQueryParamsKey(searchQueryParams)}|${buildStableSelectedCategoriesKey(selectedCategories)}`;
		const isInitialRender = isInitialRenderRef.current;
		isInitialRenderRef.current = false;

		// Computed and stored before any early return below, so it reflects a
		// genuine viewKey transition even on renders where tracking itself is
		// suppressed (e.g. the deals-page case further down).
		const isViewTransition =
			lastSeenViewKey !== null && lastSeenViewKey !== viewKey;
		lastSeenViewKey = viewKey;

		if (isFetching) {
			fetchObservedForKeyRef.current = viewKey;
			return;
		}

		// A classifier sync (variant2/3) deliberately skips the parent fetch
		// effect because the already-fetched data is correct — only the
		// URL/category label is catching up via history.replace. isFetching
		// never flips true for that transition, so fetchObservedForKeyRef never
		// gets set for the new viewKey, and the guard below would otherwise
		// block tracking for it indefinitely. classifierSyncGenerationAtom is
		// bumped exactly once per genuine sync (see useClassifierCategorySync);
		// comparing against the last-accounted value (rather than a persistent
		// flag like wasAutoSelectedByClassifier) means this bypass applies only
		// to the transition immediately following a sync, and reverts to the
		// normal fetch-observed guard for any later, unrelated view change
		// (e.g. pagination on a classifier-selected page) — even though
		// wasAutoSelectedByClassifier would still read true then.
		const isUnobservedClassifierSync =
			classifierSyncGeneration !== lastAccountedClassifierSyncGeneration;
		const hasSelectedCategories = (selectedCategories?.length ?? 0) > 0;
		const selectedCategoryFilter = filterValues.find(
			(filterValue) => filterValue.id === CATEGORY_FILTER_ID,
		);
		const hasSelectedCategoryFilter =
			Array.isArray(selectedCategoryFilter?.value) &&
			selectedCategoryFilter.value.length > 0;
		const resolvedCategories = resolveSelectedCategoryCodes({
			selectedCategories,
			categories,
		});
		const shouldWaitForResolvedCategories =
			hasSelectedCategoryFilter && selectedCategoryCodes.length === 0;
		const shouldWaitForClassifierAutoSelection =
			isUnobservedClassifierSync &&
			hasSelectedCategories &&
			!wasAutoSelectedByClassifier;

		const isNewView =
			lastTrackedViewKey !== null && lastTrackedViewKey !== viewKey;

		if (
			isViewTransition &&
			!isInitialRender &&
			fetchObservedForKeyRef.current !== viewKey &&
			!isUnobservedClassifierSync
		) {
			return;
		}

		// During classifier-driven sync, classifierSelectedCategoryIds is set
		// before filterValues settles. wasAutoSelectedByClassifier is derived from
		// filterValues and can briefly read false, which would emit an initial
		// Vendor Category Viewed with was_auto_selected:false followed by a second
		// emission with true after sync settles. Wait for settle and keep the
		// generation unaccounted so the bypass still applies on the next render.
		if (shouldWaitForClassifierAutoSelection) {
			return;
		}

		if (isUnobservedClassifierSync) {
			lastAccountedClassifierSyncGeneration = classifierSyncGeneration;
		}

		if (isNewView) {
			hasTrackedCategoryView = false;
		}

		if (hasTrackedCategoryView) {
			return;
		}
		if (shouldWaitForResolvedCategories) {
			return;
		}
		const viewedCategories = isAllCategoriesPage
			? hasSelectedCategoryFilter
				? selectedCategoryCodes
				: resolvedCategories.codes
			: resolvedCategories.codes.length > 0
				? resolvedCategories.codes
				: hasSelectedCategoryFilter
					? selectedCategoryCodes
					: [category.code];

		if (
			isAllCategoriesPage &&
			(selectedCategories ?? []).length > 0 &&
			resolvedCategories.resolutionFailed
		) {
			return;
		}

		// marketCode is read from state.search (not state.settings): it's set
		// exclusively by FETCH_SEARCH_RESULTS_SUCCESS for the *current* result
		// set and defaults to '' in search's initialState. state.settings.marketCode
		// is unsuitable here — Storefront independently dispatches
		// setVendorReferredMarketCode on mount (SET_VENDOR_REFERRED_MARKET_CODE),
		// so it can already be populated (from a prior Storefront visit in the
		// same SPA session) before this page's own search results have loaded,
		// letting this hook fire early and lock the module-level guard.
		if (!marketCode) {
			return;
		}

		if (!isSemanticSearchCalled && pageType === 'deals') {
			return;
		}

		const memberId = member ? member.id : '';
		const sourcePage = resolveVendorSearchSourcePage({
			isSemanticSearch: isSemanticSearchCalled,
			isAllCategoriesPage,
		});
		const classifierUsed = classifierMetadata.confidence != null;
		const hasClassifierPredictionInView =
			typeof classifierMetadata.prediction === 'string' &&
			viewedCategories.includes(classifierMetadata.prediction);
		const shouldWaitForClassifierSettleOnce =
			classifierUsed &&
			hasSelectedCategories &&
			hasClassifierPredictionInView &&
			!wasAutoSelectedByClassifier &&
			!hasTrackedCategoryView &&
			!classifierAutoSelectionSettleWaitedViewKeys.has(viewKey);

		if (shouldWaitForClassifierSettleOnce) {
			classifierAutoSelectionSettleWaitedViewKeys.add(viewKey);
			return;
		}

		const properties = {
			classifier_confidence: classifierMetadata.confidence,
			classifier_used: classifierUsed,
			displayLocationName: `${location.city}, ${location.stateCode}`,
			displayMarketCode: marketCode,
			memberId,
			pageNumber: currentPage,
			sourcePage,
			vendorResultsCount: totalVendors,
			vendorCategoryCode: viewedCategories.join(','),
			vendorCategoryLevel: isSemanticSearchCalled ? 'semantic' : 'city',
			...(isSemanticSearchCalled
				? {
						searchTerm: semanticSearchTerm,
					}
				: {
						badges: getBadgeCounts(vendors),
						vendorsWithDealsCount: getVendorsWithDealsCount(vendors),
					}),
			was_auto_selected: wasAutoSelectedByClassifier,
		};

		track({
			event: AnalyticsConstants.VENDOR_CATEGORY_VIEWED,
			properties,
		});

		hasTrackedCategoryView = true;
		lastTrackedViewKey = viewKey;
		classifierAutoSelectionSettleWaitedViewKeys.delete(viewKey);
	}, [
		isFetching,
		pageType,
		member,
		isSemanticSearchCalled,
		semanticSearchTerm,
		vendors,
		location,
		marketCode,
		currentPage,
		category.code,
		history.location.pathname,
		totalVendors,
		searchQueryParams,
		categories,
		filterValues,
		selectedCategoryCodes,
		selectedCategories,
		classifierMetadata,
		wasAutoSelectedByClassifier,
		isAllCategoriesPage,
		classifierSyncGeneration,
		track,
	]);

	// Reset the tracked flag (not lastTrackedViewKey — see
	// resetTrackedFlagForNavigation's doc comment) when this hook's own tree
	// unmounts due to genuine SPA navigation away from this page (e.g.
	// clicking into a storefront, then returning via the back button to the
	// same viewKey) so a legitimate new page view isn't suppressed.
	//
	// The reset is deferred to the effect's cleanup rather than done
	// immediately in the `history.listen` callback: Redux page identity
	// (location/category/pagination — part of this hook's `viewKey`) is
	// synced from the URL in a separate, later-running effect in
	// WrappedVendorsSearch, so `history.listen`'s callback (which fires
	// synchronously on push/pop) runs before that sync. `history.listen` also
	// fires for in-page navigations that keep this tree mounted (e.g.
	// Pagination's `history.push`, or a sort-only push — sort isn't part of
	// `viewKey`) — resetting immediately for those would clear the guard
	// while `viewKey` hasn't caught up yet (or won't ever change), letting a
	// stale render refire the event for the page being left. Filter changes
	// don't hit this path at all: they change `viewKey` directly (via
	// `searchQueryParams`), so they're handled by the `isNewView` check
	// above instead. Only checking the last recorded action at cleanup time
	// (i.e. when this tree actually unmounts, or `history` changes identity)
	// limits the reset to genuine navigation-away, and — like before — only
	// for 'PUSH'/'POP', never 'REPLACE' (the in-place URL sync used
	// internally by useClassifierCategorySync).
	useEffect(() => {
		let lastNavigationAction: string | null = null;
		let lastNavigationPathname: string | null = null;
		const mountPathname = history.location.pathname;

		const unlisten = history.listen((nextLocation, action) => {
			lastNavigationAction = action;
			lastNavigationPathname = nextLocation.pathname;
		});

		return () => {
			unlisten();
			if (
				(lastNavigationAction === 'PUSH' || lastNavigationAction === 'POP') &&
				lastNavigationPathname != null &&
				lastNavigationPathname !== mountPathname
			) {
				resetTrackedFlagForNavigation();
			}
		};
	}, [history]);
}
